import React, { useState, useEffect } from "react";
import {
  Mail,
  Send,
  CheckCircle,
  Sparkles,
  Users,
  Bell,
  Heart,
  Globe,
  Leaf,
  Award,
  Zap,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  Info,
  Star,
  TrendingUp,
  Shield,
  Truck
} from "lucide-react";


const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsSubscribed(true);
      setEmail("");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResubscribe = () => {
    setIsSubscribed(false);
    setError("");
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-20 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="relative py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!isSubscribed ? (
            <div className="text-center">
              {/* Header Section */}
              <div className="mb-12">
                <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200 rounded-full px-6 py-3 mb-6">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-700">Stay Updated</span>
                </div>
                
                <h2 className="text-5xl font-bold bg-gradient-to-r from-gray-900 via-green-800 to-emerald-800 bg-clip-text text-transparent mb-6">
                  Join Our Newsletter
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  Get the latest updates on eco-friendly products, sustainability tips, 
                  and exclusive offers delivered straight to your inbox.
                </p>
              </div>

              {/* Newsletter Form */}
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 lg:p-12">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                      <Mail className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Stay Connected</h3>
                    <p className="text-gray-600">Be the first to know about our latest eco-friendly innovations</p>
                  </div>

                  <div className="max-w-md mx-auto">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-lg"
                        placeholder="Enter your email address"
                        disabled={isLoading}
                      />
                    </div>
                    
                    {error && (
                      <div className="mt-4 flex items-center space-x-2 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">{error}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`group relative px-12 py-4 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${
                        isLoading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                      }`}
                    >
                      {isLoading ? (
                        <div className="flex items-center space-x-3">
                          <RefreshCw className="w-6 h-6 animate-spin" />
                          <span>Subscribing...</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3">
                          <Send className="w-6 h-6" />
                          <span>Subscribe Now</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </div>
                      )}
                    </button>
                  </div>

                  {/* Benefits */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                    <div className="text-center group">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Bell className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Latest Updates</h4>
                      <p className="text-sm text-gray-600">Get notified about new products and features</p>
                    </div>
                    
                    <div className="text-center group">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Exclusive Offers</h4>
                      <p className="text-sm text-gray-600">Special discounts for newsletter subscribers</p>
                    </div>
                    
                    <div className="text-center group">
                      <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Leaf className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Eco Tips</h4>
                      <p className="text-sm text-gray-600">Sustainability tips and environmental news</p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            /* Success State */
            <div className="text-center">
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-12">
                <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
                
                <h3 className="text-4xl font-bold text-gray-900 mb-4">Welcome Aboard!</h3>
                <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                  Thank you for subscribing to our newsletter. You'll receive our latest updates, 
                  exclusive offers, and eco-friendly tips delivered to your inbox.
                </p>

                {/* Success Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="flex items-center space-x-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Email Confirmed</h4>
                      <p className="text-sm text-gray-600">Your subscription is active</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4">
                    <Bell className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Notifications On</h4>
                      <p className="text-sm text-gray-600">You'll receive our updates</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleResubscribe}
                  className="group relative bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-8 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
                    <span>Subscribe Another Email</span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Newsletter;
